import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ActionIcon, Button, Code, CopyButton, Group, Modal, Stack, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCheck, IconCopy, IconEye, IconEyeOff, IconInfoCircle } from "@tabler/icons-react";
import { useState } from "react";
const SENSITIVE_KEYS = ["ANON_KEY", "SERVICE_KEY", "MONITORING_TOKEN", "SECRET", "TOKEN", "KEY"];
function isSensitive(key) {
    return SENSITIVE_KEYS.some((sensitive) => key.toUpperCase().includes(sensitive));
}
function maskValue(value) {
    if (value.length <= 8)
        return "••••••••";
    return `${value.slice(0, 4)}${"•".repeat(value.length - 8)}${value.slice(-4)}`;
}
export function EnvironmentInfo({ env, requiredRole = "admin", userRole }) {
    const [opened, { open, close }] = useDisclosure(false);
    const [visibleKeys, setVisibleKeys] = useState({});
    const toggleVisibility = (key) => {
        setVisibleKeys((prev) => {
            if (prev[key]) {
                const { [key]: _removed, ...rest } = prev;
                return rest;
            }
            return { ...prev, [key]: true };
        });
    };
    // Filter out undefined values and sort by key name
    const envEntries = Object.entries(env)
        .filter(([, value]) => value !== undefined)
        .sort(([a], [b]) => a.localeCompare(b));
    // Check if user has required role
    const hasAccess = !requiredRole || userRole === requiredRole;
    return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "subtle", fullWidth: true, leftSection: _jsx(IconInfoCircle, { size: 16 }), onClick: open, disabled: !hasAccess, children: "Environment Info" }), _jsx(Modal, { opened: opened, onClose: close, title: "Environment Configuration", size: "lg", children: _jsxs(Stack, { gap: "md", children: [_jsx(Text, { size: "sm", c: "dimmed", children: "Current environment variables configured for this application:" }), envEntries.map(([key, value]) => {
                            const sensitive = isSensitive(key);
                            const isVisible = visibleKeys[key] === true;
                            const displayValue = sensitive && !isVisible ? maskValue(value) : value;
                            return (_jsxs(Stack, { gap: "xs", children: [_jsxs(Group, { justify: "space-between", children: [_jsx(Text, { size: "sm", fw: 500, children: key }), _jsxs(Group, { gap: "xs", children: [sensitive && (_jsx(ActionIcon, { variant: "subtle", size: "sm", onClick: () => toggleVisibility(key), "aria-label": isVisible ? "Hide value" : "Show value", children: isVisible ? _jsx(IconEyeOff, { size: 16 }) : _jsx(IconEye, { size: 16 }) })), _jsx(CopyButton, { value: value, timeout: 2000, children: ({ copied, copy }) => (_jsx(Tooltip, { label: copied ? "Copied" : "Copy", withArrow: true, position: "right", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", onClick: copy, "aria-label": "Copy value", children: copied ? _jsx(IconCheck, { size: 16 }) : _jsx(IconCopy, { size: 16 }) }) })) })] })] }), _jsx(Code, { block: true, style: {
                                            wordBreak: "break-all",
                                            whiteSpace: "pre-wrap",
                                            fontSize: "0.75rem",
                                        }, children: displayValue })] }, key));
                        }), _jsx(Group, { justify: "flex-end", mt: "md", children: _jsx(Button, { onClick: close, children: "Close" }) })] }) })] }));
}
